import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ContentSection, SectionHeader } from '../components/sections/ContentSection';
import { MinistryCard } from '../components/cards/MinistryCard';
import { EventCard } from '../components/cards/EventCard';
import { PhotoGallery } from '../components/gallery/PhotoGallery';
import { usePublicData } from '../hooks/usePublicData';
import { SEO } from '../components/SEO';
import { parseMarkdownLinks } from '../utils/sanitize';
import { ArrowRight, Heart, Users, BookOpen, Coffee, Loader2, Play, Calendar, Music, Cross, Star, Church } from 'lucide-react';

// Icon mapping for feature cards
const iconMap = {
  heart: Heart,
  users: Users,
  book: BookOpen,
  coffee: Coffee,
  calendar: Calendar,
  music: Music,
  cross: Cross,
  star: Star,
  church: Church
};

// Fallback data in case API fails
const fallbackMinistries = [
  {
    title: "Serve & Be Served",
    description: "Learn how we serve the community and help you grow individually.",
    imageUrl: "https://customer-assets.emergentagent.com/job_geneva-faith-site/artifacts/18zi1gt4_ministries.jpg",
    link: "/ministries"
  },
  {
    title: "Children's Ministry",
    description: "Nurturing young hearts with engaging Bible lessons, fun activities, and a loving environment where children grow in faith.",
    imageUrl: "https://images.unsplash.com/photo-1717931699152-fbab890f5428",
    link: "/sunday-school"
  },
  {
    title: "Daycare & Preschool",
    description: "Open Monday – Friday from 6:00 am to 6:00 pm, we provide quality care for children ages 18 months through 12 years of age.",
    imageUrl: "https://customer-assets.emergentagent.com/job_geneva-faith-site/artifacts/jexw80au_daycare-2-classroom-no-people.jpg",
    link: "/ministries"
  }
];

// Default homepage settings
const defaultHomepage = {
  hero: {
    backgroundImage: "https://customer-assets.emergentagent.com/job_geneva-faith-site/artifacts/ffjjquf2_Church-outside-overview-photo.png",
    title: "Open Hearts, Open Minds, Open Doors",
    subtitle: "We welcome all who seek to love the Lord",
    tagline: "Geneva United Methodist Church",
    serviceTimes: [
      { label: "Sunday Worship", time: "9:30 AM" },
      { label: "Sunday School", time: "10:45 AM" }
    ],
    buttons: [
      { text: "Plan Your Visit", link: "/welcome", style: "primary" },
      { text: "Contact Us", link: "/contact", style: "outline" }
    ]
  },
  welcome: {
    image: "https://customer-assets.emergentagent.com/job_geneva-faith-site/artifacts/uw8747ga_Church-sanctuary-people.jpg",
    badgeTitle: "100+ Years",
    badgeSubtitle: "Serving Geneva",
    sectionLabel: "Welcome to Our Church",
    title: "A Place Where",
    highlightText: "Everyone Belongs",
    paragraphs: [
      "At Geneva United Methodist Church, we believe in the power of community, the importance of faith, and the joy of serving others. Whether you're seeking spiritual growth, meaningful connections, or simply a welcoming place to worship, you'll find a home here.",
      "Led by Pastor Pam Buzalka, our congregation is dedicated to living out our mission of Open Hearts, Open Minds, and Open Doors. We invite you to experience the warmth of our community and discover what makes our church special."
    ],
    buttons: [
      { text: "Learn More About Us", link: "/welcome", style: "primary" },
      { text: "Get in Touch", link: "/contact", style: "outline" }
    ]
  }
};

// Helper function to format date for display
const formatEventDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  return `${month} ${day}`;
};

// Dynamic Hero Component
const DynamicHero = ({ settings }) => {
  const hero = settings?.hero || defaultHomepage.hero;
  
  return (
    <section 
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={hero.backgroundImage}
          alt="Geneva United Methodist Church"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-32">
        <div className="max-w-3xl">
          {/* Tagline */}
          {hero.tagline && (
            <p className="inline-block px-4 py-2 bg-secondary/20 backdrop-blur-sm text-secondary font-medium text-sm rounded-full mb-6 animate-fade-up">
              {hero.tagline}
            </p>
          )}

          {/* Title */}
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6 animate-fade-up animate-delay-100">
            {hero.title}
          </h1>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-slate-200 mb-8 leading-relaxed animate-fade-up animate-delay-200">
            {hero.subtitle}
          </p>

          {/* Service Times */}
          {hero.serviceTimes && hero.serviceTimes.length > 0 && (
            <div className="flex flex-wrap gap-4 mb-10 animate-fade-up animate-delay-300">
              {hero.serviceTimes.map((time) => (
                <div key={`service-${time.label}-${time.time}`} className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                  <p className="text-secondary font-semibold text-sm mb-1">{time.label}</p>
                  <p className="text-white font-medium">{time.time}</p>
                </div>
              ))}
            </div>
          )}

          {/* CTA Buttons */}
          {hero.buttons && hero.buttons.length > 0 && (
            <div className="flex flex-wrap gap-4 animate-fade-up animate-delay-400">
              {hero.buttons.map((btn) => (
                <Link
                  key={`hero-btn-${btn.text}-${btn.link}`}
                  to={btn.link}
                  className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold transition-all duration-300 group ${
                    btn.style === 'primary' 
                      ? 'bg-secondary text-white hover:bg-secondary-600 hover:shadow-lg hover:-translate-y-0.5' 
                      : 'bg-white/10 backdrop-blur-sm text-white border border-white/30 hover:bg-white/20'
                  }`}
                >
                  {btn.text}
                  {btn.style === 'primary' && (
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Dynamic Welcome Section
const DynamicWelcome = ({ settings }) => {
  const welcome = settings?.welcome || defaultHomepage.welcome;
  
  return (
    <ContentSection bgColor="bg-slate-50">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="order-2 lg:order-1">
          {welcome.sectionLabel && (
            <p className="text-secondary font-semibold text-sm tracking-wide uppercase mb-4">
              {welcome.sectionLabel}
            </p>
          )}
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-primary-900 mb-6">
            {welcome.title} <span className="text-gold-gradient">{welcome.highlightText}</span>
          </h2>
          
          {welcome.paragraphs && welcome.paragraphs.map((para, index) => (
            <p 
              key={`welcome-para-${index}`} 
              data-testid={`home-welcome-paragraph-${index}`}
              className={`text-slate-600 leading-relaxed ${index === 0 ? 'text-lg mb-6' : 'mb-8'} [&_a]:text-secondary [&_a]:underline [&_a]:hover:text-secondary-600`}
              dangerouslySetInnerHTML={{ __html: parseMarkdownLinks(para) }}
            />
          ))}
          
          {welcome.buttons && welcome.buttons.length > 0 && (
            <div className="flex flex-wrap gap-4">
              {welcome.buttons.map((btn) => (
                <Link
                  key={`welcome-btn-${btn.text}-${btn.link}`}
                  to={btn.link}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all group ${
                    btn.style === 'primary' 
                      ? 'bg-secondary text-white hover:bg-secondary-600 hover:shadow-lg hover:-translate-y-0.5' 
                      : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-secondary hover:text-secondary'
                  }`}
                >
                  {btn.text}
                  {btn.style === 'primary' && (
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="order-1 lg:order-2 relative">
          <img 
            src={welcome.image}
            alt="Geneva UMC congregation"
            className="rounded-2xl shadow-xl w-full"
          />
          {welcome.badgeTitle && (
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-6 max-w-[240px] hidden md:block">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="font-semibold text-primary-900">{welcome.badgeTitle}</p>
                  <p className="text-sm text-slate-500">{welcome.badgeSubtitle}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ContentSection>
  );
};

export default function Home() {
  // Fetch data from API
  const { data: homepage, loading: homepageLoading } = usePublicData('homepage');
  const { data: ministries, loading: ministriesLoading } = usePublicData('ministries/homepage');
  const { data: events, loading: eventsLoading } = usePublicData('events/upcoming');
  const { data: gallery, loading: galleryLoading } = usePublicData('gallery');

  // Use fetched data or fallback
  const displayMinistries = ministries.length > 0 ? ministries : fallbackMinistries;
  const homepageSettings = homepage && Object.keys(homepage).length > 0 ? homepage : defaultHomepage;
  
  // Transform events for EventCard component
  const displayEvents = events.map(event => ({
    title: event.title,
    date: formatEventDate(event.eventDate),
    time: event.startTime || '',
    location: event.location || '',
    description: event.description || '',
    featured: event.featured || false
  }));

  // Transform gallery for PhotoGallery component
  const displayGallery = gallery.map(item => ({
    url: item.imageUrl,
    alt: item.title,
    caption: item.caption || item.title
  }));

  return (
    <Layout>
      <SEO 
        title={null}
        description="Welcome to Geneva United Methodist Church in Geneva, Ohio. Join us for Sunday worship at 9:30 AM. A welcoming community of faith, fellowship, and service."
        url="/"
      />
      
      {/* Hero Section - Dynamic */}
      <DynamicHero settings={homepageSettings} />

      {/* Welcome Section - Dynamic */}
      <DynamicWelcome settings={homepageSettings} />

      {/* Quick Info Cards - Editable Feature Cards */}
      <ContentSection bgColor="bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {(homepageSettings?.featureCards || [
            { icon: "users", title: "Welcoming Community", description: "All are welcome here", link: "/welcome" },
            { icon: "book", title: "Bible-Based Teaching", description: "Growing in faith together", link: "/sunday-school" },
            { icon: "heart", title: "Fellowship", description: "Building lasting friendships", link: "/ministries" }
          ]).map((item) => {
            const IconComponent = iconMap[item.icon] || Heart;
            const isExternal = item.link && item.link.startsWith('http');
            const stableKey = `feature-${item.title}-${item.link || 'no-link'}`;
            
            const cardContent = (
              <>
                <div className="w-14 h-14 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-secondary/20 transition-colors">
                  <IconComponent className="w-7 h-7 text-secondary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-primary-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.description}</p>
              </>
            );

            const cardClass = `bg-slate-50 rounded-xl p-6 text-center hover:bg-white hover:shadow-lg transition-all duration-300 group border border-transparent hover:border-secondary/20 ${item.link ? 'cursor-pointer' : ''}`;
            
            if (!item.link) {
              return <div key={stableKey} className={cardClass} data-testid={`quick-info-${item.title}`}>{cardContent}</div>;
            } else if (isExternal) {
              return (
                <a key={stableKey} href={item.link} target="_blank" rel="noopener noreferrer" className={cardClass} data-testid={`quick-info-${item.title}`}>
                  {cardContent}
                </a>
              );
            } else {
              return (
                <Link key={stableKey} to={item.link} className={cardClass} data-testid={`quick-info-${item.title}`}>
                  {cardContent}
                </Link>
              );
            }
          })}
        </div>
      </ContentSection>

      {/* Ministries Section */}
      <ContentSection bgColor="bg-slate-50">
        <SectionHeader 
          title="Our Ministries"
          subtitle="Discover ways to get involved and grow in your faith journey with us"
        />
        {ministriesLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-secondary" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayMinistries.map((ministry, index) => (
              <MinistryCard 
                key={ministry.id || index} 
                title={ministry.title}
                description={ministry.description}
                image={ministry.imageUrl || ministry.image}
                link={ministry.link || '/ministries'}
              />
            ))}
          </div>
        )}
        <div className="text-center mt-10">
          <Link
            to="/ministries"
            className="inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all"
            data-testid="ministries-view-all-btn"
          >
            View All Ministries
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </ContentSection>

      {/* Events Section */}
      <ContentSection bgColor="bg-white">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <SectionHeader 
              title="Upcoming Events"
              subtitle="Join us for worship, fellowship, and community gatherings"
              align="left"
            />
            {eventsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-secondary" />
              </div>
            ) : displayEvents.length > 0 ? (
              <div className="space-y-4">
                {displayEvents.slice(0, 4).map((event) => (
                  <EventCard key={`event-${event.title}-${event.date}`} {...event} featured={event.featured} />
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No upcoming events scheduled.</p>
            )}
            <div className="mt-8">
              <Link
                to="/calendar"
                className="inline-flex items-center gap-2 bg-secondary text-white px-6 py-3 rounded-full font-semibold hover:bg-secondary-600 transition-all hover:shadow-lg hover:-translate-y-0.5 group"
                data-testid="events-view-calendar-btn"
              >
                View Full Calendar
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
          
          {/* Service Times Card */}
          <div className="bg-primary-900 rounded-2xl p-8 lg:p-10 text-white self-start">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-6">
              Join Us This Sunday
            </h3>
            <div className="space-y-6">
              {(homepageSettings?.hero?.serviceTimes || defaultHomepage.hero.serviceTimes).map((time) => (
                <div key={`sidebar-service-${time.label}-${time.time}`} className="border-l-4 border-secondary pl-5">
                  <p className="text-secondary font-semibold mb-1">{time.label}</p>
                  <p className="text-2xl font-bold">{time.time}</p>
                  {time.description && <p className="text-slate-300 text-sm mt-1">{time.description}</p>}
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-slate-300 text-sm mb-4">
                Junior Church and Nursery available. Handicapped access and elevator available from rear entrance.
              </p>
              <Link
                to="/welcome"
                className="inline-flex items-center gap-2 bg-secondary text-white px-6 py-3 rounded-full font-semibold hover:bg-secondary-600 transition-all"
                data-testid="service-times-plan-visit-btn"
              >
                Plan Your Visit
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* Gallery Preview */}
      <ContentSection bgColor="bg-slate-50">
        <SectionHeader 
          title="Life at Our Church"
          subtitle="A glimpse into our community and the moments we share together"
        />
        {galleryLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-secondary" />
          </div>
        ) : displayGallery.length > 0 ? (
          <PhotoGallery images={displayGallery.slice(0, 4)} columns={4} />
        ) : (
          <p className="text-slate-500 text-center py-8">Gallery coming soon.</p>
        )}
        <div className="text-center mt-10">
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 text-secondary font-semibold hover:gap-3 transition-all"
            data-testid="gallery-view-all-btn"
          >
            View Full Gallery
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </ContentSection>

      {/* CTA Section */}
      <ContentSection bgColor="bg-secondary" paddingY="py-16 md:py-20">
        <div className="text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6">
            Ready to Experience Our Community?
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
            Whether you're visiting for the first time or looking for a church home, 
            we'd love to welcome you this Sunday.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/welcome"
              className="inline-flex items-center gap-2 bg-white text-secondary px-8 py-4 rounded-full font-semibold hover:bg-slate-50 transition-all hover:shadow-lg group"
              data-testid="cta-plan-visit-btn"
            >
              Plan Your Visit
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-transparent text-white border-2 border-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all"
              data-testid="cta-contact-btn"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </ContentSection>
    </Layout>
  );
}
